import { captureManufacturing, suppliedManufacturing } from "./economy-manufacturing.js";
import {
  add,
  constraintDiagnostic,
  minMicro,
  multiply,
  ratioPortion,
  subtract,
  ZERO,
} from "./economy-math.js";
import { type PowerResult, settlePower } from "./economy-power.js";
import { captureProduction } from "./economy-production.js";
import { buildingRule, unitRule } from "./economy-rules.js";
import {
  ECONOMY_BUILDINGS,
  type EconomyDiagnostics,
  type EconomyInput,
  type EconomySettlement,
  type MetalLane,
} from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { effectMultiplier } from "./research-effects.js";

export function settleEconomy(input: EconomyInput): EconomySettlement {
  validateInput(input);
  if (!input.alive) return frozenSettlement(input);
  const power = settlePower(input);
  const production = captureProduction(input, power.operation);
  const producerDemand = add(
    ...production.intended.map(({ recipe, output }) => multiply(output, recipe.metalPerCapacity)),
  );
  const manufacturing = captureManufacturing(
    input,
    production.constructionWork,
    production.assemblyWork,
    producerDemand,
  );
  const reservations = reserveMetal(input.metal, manufacturing.demand, input.metalPriority);
  const progress = suppliedManufacturing(manufacturing, reservations);
  const producerOutput = production.intended.map(({ recipe, output }) => ({
    recipe,
    output: ratioPortion(output, reservations["producer-factories"], producerDemand),
  }));
  const buildings = { ...input.buildings };
  const wreckage = { ...input.wreckage };
  const units = { ...input.units };
  const plan = input.manufacturing;
  if (plan.newConstruction)
    buildings[plan.newConstruction.target] = add(
      buildings[plan.newConstruction.target],
      progress.newConstruction,
    );
  if (plan.restoration) {
    buildings[plan.restoration.target] = add(
      buildings[plan.restoration.target],
      progress.restoration,
    );
    wreckage[plan.restoration.target] = subtract(
      wreckage[plan.restoration.target],
      progress.restoration,
    );
  }
  if (plan.assembly)
    units[plan.assembly.target] = add(units[plan.assembly.target], progress.assembly);
  for (const { recipe, output } of producerOutput)
    buildings[recipe.output] = add(buildings[recipe.output], output);
  const spent = actualMetalSpent(input, progress, producerOutput);
  return Object.freeze({
    metal: add(subtract(input.metal, spent), production.mined),
    researchPoints: add(input.researchPoints, production.research),
    units: Object.freeze(units),
    buildings: Object.freeze(buildings),
    wreckage: Object.freeze(wreckage),
    progress: Object.freeze(progress),
    diagnostics: diagnosticsFor(
      input,
      power,
      production,
      manufacturing.demand,
      reservations,
      spent,
    ),
  });
}

function actualMetalSpent(
  input: EconomyInput,
  progress: EconomySettlement["progress"],
  producers: readonly { recipe: { metalPerCapacity: string }; output: Micro }[],
): Micro {
  const plan = input.manufacturing;
  return add(
    plan.newConstruction
      ? multiply(
          progress.newConstruction,
          String(buildingRule(plan.newConstruction.target).metalCost),
        )
      : ZERO,
    plan.restoration
      ? multiply(
          multiply(progress.restoration, String(buildingRule(plan.restoration.target).metalCost)),
          plan.restorationMetalRatio,
        )
      : ZERO,
    plan.assembly
      ? multiply(progress.assembly, String(unitRule(plan.assembly.target).metalCost))
      : ZERO,
    ...producers.map(({ recipe, output }) => multiply(output, recipe.metalPerCapacity)),
  );
}

function reserveMetal(
  stock: Micro,
  demand: Record<MetalLane, Micro>,
  priority: readonly MetalLane[],
): Record<MetalLane, Micro> {
  let remaining = stock;
  const result = { construction: ZERO, "producer-factories": ZERO, "troop-assembly": ZERO };
  for (const lane of priority) {
    result[lane] = minMicro(remaining, demand[lane]);
    remaining = subtract(remaining, result[lane]);
  }
  return result;
}

function diagnosticsFor(
  input: EconomyInput,
  power: PowerResult,
  production: ReturnType<typeof captureProduction>,
  demand: Record<MetalLane, Micro>,
  received: Record<MetalLane, Micro>,
  spent: Micro,
): EconomyDiagnostics {
  const operatedShieldGenerators = ratioPortion(
    input.buildings["shield-generator"],
    power.operation.defenses[0],
    power.operation.defenses[1],
  );
  return Object.freeze({
    availablePower: power.available,
    unusedPower: power.unused,
    power: power.diagnostics,
    metal: Object.freeze(
      Object.fromEntries(
        input.metalPriority.map((lane) => [
          lane,
          constraintDiagnostic("Metal", demand[lane], received[lane]),
        ]),
      ),
    ) as EconomyDiagnostics["metal"],
    grossMetal: production.mined,
    metalSpent: spent,
    netMetal: subtract(production.mined, spent),
    constructionWork: production.constructionWork,
    assemblyWork: production.assemblyWork,
    researchPoints: production.research,
    shieldRecharge: production.shieldRecharge,
    shieldCapacity: multiply(
      operatedShieldGenerators,
      formatMultiplier(200, effectMultiplier(input.researchEffects, "shield-capacity")),
    ),
    operatedShieldGenerators,
    supplyCapacity: add(
      microFromString("100"),
      ratioPortion(
        multiply(
          multiply(input.buildings["supply-depot"], "40"),
          effectMultiplier(input.researchEffects, "supply-depot-capacity"),
        ),
        power.operation.defenses[0],
        power.operation.defenses[1],
      ),
    ),
  });
}

function frozenSettlement(input: EconomyInput): EconomySettlement {
  return Object.freeze({
    metal: input.metal,
    researchPoints: input.researchPoints,
    units: input.units,
    buildings: input.buildings,
    wreckage: input.wreckage,
    progress: Object.freeze({ newConstruction: ZERO, restoration: ZERO, assembly: ZERO }),
    diagnostics: emptyDiagnostics(input),
  });
}

function emptyDiagnostics(input: EconomyInput): EconomyDiagnostics {
  const power = Object.freeze(
    Object.fromEntries(
      input.powerPriority.map((id) => [id, constraintDiagnostic("Power", ZERO, ZERO)]),
    ),
  ) as EconomyDiagnostics["power"];
  const metal = Object.freeze(
    Object.fromEntries(
      input.metalPriority.map((id) => [id, constraintDiagnostic("Metal", ZERO, ZERO)]),
    ),
  ) as EconomyDiagnostics["metal"];
  return Object.freeze({
    availablePower: ZERO,
    unusedPower: ZERO,
    power,
    metal,
    grossMetal: ZERO,
    metalSpent: ZERO,
    netMetal: ZERO,
    constructionWork: ZERO,
    assemblyWork: ZERO,
    researchPoints: ZERO,
    shieldRecharge: ZERO,
    shieldCapacity: ZERO,
    operatedShieldGenerators: ZERO,
    supplyCapacity: ZERO,
  });
}

function formatMultiplier(base: number, multiplier: string): string {
  return formatMicro(multiply(microFromString(String(base)), multiplier));
}

function validateInput(input: EconomyInput): void {
  if (!Number.isSafeInteger(input.elapsedMs) || input.elapsedMs < 0)
    throw new TypeError("elapsedMs must be a nonnegative safe integer");
  const quantities = [
    input.metal,
    input.researchPoints,
    ...Object.values(input.units),
    ...ECONOMY_BUILDINGS.flatMap((id) => [input.buildings[id], input.wreckage[id]]),
  ];
  if (quantities.some((value) => value < 0n))
    throw new TypeError("Economy quantities cannot be negative");
  validatePercentages(Object.values(input.powerSliders), false);
  validatePercentages(Object.values(input.factoryWorksSplit), true);
  validatePercentages(Object.values(input.manufacturing.constructionSplit), true);
  if (
    !sameMembers(input.powerPriority, [
      "industry",
      "defenses",
      "military-production",
      "research",
    ]) ||
    !sameMembers(input.metalPriority, ["construction", "producer-factories", "troop-assembly"])
  )
    throw new TypeError("Priorities must contain every lane exactly once");
}

function sameMembers(value: readonly string[], expected: readonly string[]): boolean {
  return (
    value.length === expected.length &&
    new Set(value).size === expected.length &&
    value.every((entry) => expected.includes(entry))
  );
}

function validatePercentages(values: readonly number[], totalLimited: boolean): void {
  if (
    values.some((value) => !Number.isInteger(value) || value < 0 || value > 100) ||
    (totalLimited && values.reduce((sum, value) => sum + value, 0) > 100)
  )
    throw new TypeError("Allocations must be whole percentages from 0 to 100");
}
